import React, { useEffect, useState } from "react";
import { SettingOutlined, PlusOutlined } from "@ant-design/icons";
import { Table, Button, Modal, Pagination, Tag, Input, Select, ConfigProvider, Empty } from "antd";
import { useNavigate } from 'react-router-dom';
import { Tooltip } from "antd";
import Header from "../../../component/menu/Header";
import CustomLoader from "../../../component/menu/CustomLoader";
import useUserStore from "../../../stores/store";
import ApiService from "../../../utils/ApiService";
import { PAGINATION_PAGE_OPTIONS } from "../../../constant/constants";


export default function CouponSettingsList() {
  const navigate = useNavigate();
  const { restaurantId, user, networkCall, setNetworkCall, showNotification } = useUserStore();
  const [couponList, setCouponList] = useState([]);
  const [searchKey, setSearchKey] = useState();
  const [status, setStatus] = useState();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ restaurantId: restaurantId, searchKey: "", status: "", page: 1, perPage: 10 });

  // --- ACTIONS ---
  const handleAddNew = () => {
    navigate('/coupon-settings/coupon/add/');
  };

  const handleEdit = (record) => {
    navigate(`/coupon-settings/coupon/${record.id}/edit`);
  };

  const handleRelaunch = (record) => {
    navigate(`/coupon-settings/coupon/${record.id}/relaunch`);
  };

  async function couponDelete(couponId) {
    setNetworkCall(true);
    try {
      const res = await ApiService.COUPON_DELETE(couponId, user.token.token);
      if (res.code !== 200) {
        setNetworkCall(false);
        showNotification(res.message);
        return;
      }
      
      setNetworkCall(false);
      fetchCouponList(filter);
      showNotification(`Coupon delete successfully.`, false);
      return;
    } catch (err) {
      setNetworkCall(false);
      showNotification(err.response.data.message);
      return;
    }
  }

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Are you sure you want to delete this coupon?",
      content: "This action cannot be undone. A dependency check will be performed before deletion.",
      onOk: () => {
        couponDelete(record.id);
      }
    });
  };

  function onHandleSearchKey(e) {
    const val = e.target.value;
    setSearchKey(val);
  }

  function getModifiedCouponListData(data) {
    if (Boolean(data) && !Array.isArray(data) && data.length < 1) return;
    return data.map((el) => ({
      id: el.id,
      title: el.title,
      coupon_code: el.code,
      discount_type: el.discount.isDiscountInPercent ? 'PERCENT' : 'AMOUNT',
      discount: `${el.discount.discount} (${el.discount.isDiscountInPercent ? '%' : 'TK'})`,
      min_order_amount: el.minOrderAmount,
      max_discount_amount: el.discount.maxDiscountAmount,
      status: el.status,
      start_time: el.validFrom,
      end_time: el.validUntil,
      value_share: el.valueSharePercentage,
      validity: prepareValidity(el.validFrom, el.validUntil),
    }));
  }

  const prepareValidity = (validFrom = '', validUntil = '') => {
    if (!validFrom || !validUntil) return '';

    const formatDate = (dateString) => {
      const date = new Date(dateString);

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');

      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // 0 should be 12

      return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
    };

    return `${formatDate(validFrom)} - ${formatDate(validUntil)}`;
  };

  async function fetchCouponList(filter) {
    setNetworkCall(true);
    try {
      const response = await ApiService.GET_COUPON_LIST(filter, user.token.token);

      if (response.code !== 200) {
        setNetworkCall(false);
        showNotification(response.message || "Someting went wrong. Please try again later.");
        return;
      }
      const payload = response.payload;
      setPage(payload.page);
      setPerPage(payload.perPage);
      setTotal(payload.total);

      if (payload.records.length < 1){
        setCouponList([])
      }

      if (Boolean(payload.records) && payload.records.length > 0) {
        setCouponList(getModifiedCouponListData(payload.records));
      }

      setNetworkCall(false);
    } catch (err) {
      showNotification("Something went wrong! Please try again later.");
      setNetworkCall(false);
      return;
    }
  }

  function handleApplySearch() {
    setFilter(prev => ({
      ...prev,
      searchKey: searchKey || "",
      status: status || "",
    }))
  }

  function handleCancelSearch() {
    setFilter(prev => ({
      ...prev,
      searchKey: "",
      status: "",
      page: 1,
      perPage: 10
    }));
    setSearchKey(null);
    setStatus(null);
    setPage(1);
    setPerPage(10);
  }

  function handlePageChange(page, pageSize) {
    const updatedFilter = { ...filter, page: page, perPage: pageSize };
    fetchCouponList(updatedFilter);
    setPage(page);
    setPerPage(pageSize);
  }

  useEffect(() => {
    fetchCouponList(filter);
  }, [filter]);



  // --- TABLE COLUMNS ---
  const columns = [
    {
      title: 'Coupon Title',
      dataIndex: 'title',
      key: 'title',
      align: 'left',
      render: (text) => (
        <Tooltip title={text.length > 20 ? text : ""}>
          <span style={{ cursor: 'default' }}>
            {text.length > 20 ? `${text.substring(0, 20)}...` : text}
          </span>
        </Tooltip>
      )
    },
    { title: 'Coupon Code', dataIndex: 'coupon_code', key: 'coupon_code', align: 'center', render: text => <Tag color="blue">{text}</Tag> },
    // { title: 'Discount', dataIndex: 'discount_type', key: 'type', align: 'center' },
    { title: 'Discount', dataIndex: 'discount', key: 'discount', align: 'center' },
    { title: 'Min. Order Amount', dataIndex: 'min_order_amount', key: 'min_order_amount', align: 'center' },
    { title: 'Max. Discount Amount', dataIndex: 'max_discount_amount', key: 'max_discount_amount', align: 'center' },
    { title: 'Value Share (%)', dataIndex: 'value_share', key: 'value_share', align: 'center' },
    { title: 'Validity', dataIndex: 'validity', key: 'validity', align: 'center' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (text) => (
        <Tag color={
            text === 'Active' ? 'green' :
            text === 'Inactive' ? 'red' :
            text === 'Expired' ? 'red' :
            text === 'Upcoming' ? 'orange' : 'gray'
        }>
          {text}
        </Tag>
      )
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      render: (_, record) => (
        <div className="flex gap-2 justify-center">
          {
            record.status === 'Expired' ? (
              <Tooltip placement="bottom" title="Relaunch Coupon">
                <img
                  onClick={() => handleRelaunch(record)}
                  className="w-[30px] h-[30px] ml-[6px] cursor-pointer"
                  src={"/images/reset-icon.svg"}
                />
              </Tooltip>
            ):(
              <Tooltip placement="bottom" title="Edit Coupon">
                <img
                  onClick={() => handleEdit(record)}
                  className="w-[30px] h-[30px] ml-[-3px] cursor-pointer"
                  src={"https://tabletap-cdn.s3.ap-south-1.amazonaws.com/tabletap-prod/static-assets/edit_user_icon.svg"}
                />
              </Tooltip>
            )
          }


          <Tooltip placement="bottom" title="Delete Coupon">
            <img
              onClick={() => handleDelete(record)}
              className="w-[30px] h-[30px] ml-[6px] cursor-pointer"
              src={"/images/deleteicon.svg"}
            />
          </Tooltip>
        </div>
      )
    },
  ];

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <CustomLoader />
      <Header title={"Coupon Settings"} />

      <div className="flex flex-col p-[24px] w-full mt-[80px] font-uregular">
        <div className="w-[85%] m-auto bg-white shadow-lg rounded-lg overflow-hidden">

          {/* Header Section */}
          <div className="p-5 border-b">
            <div className="flex justify-between items-center">
              <div className="font-bold text-lg flex items-center">
                <SettingOutlined style={{ fontSize: 18, marginRight: 10 }} />
                Coupon Settings
              </div>
              <ConfigProvider
                theme={{
                  token: {
                    colorPrimary: "#FCBD10",
                    colorPrimaryHover: "#FCBD10",
                    colorPrimaryActive: "#FCBD10",
                    colorTextLightSolid: "#000000",
                  },
                }}
              >
                <Button className="h-[48px] text-black font-bold" type="primary" icon={<PlusOutlined />} onClick={handleAddNew}>
                  Add
                </Button>
              </ConfigProvider>
            </div>

            <div className="mt-[20px] w-[50%] flex items-center gap-x-[20px]">
              <div className="w-full flex gap-x-[20px]">
                <div className="h-[48px] w-[70%]">
                  <Input
                    className="h-full w-full"
                    value={searchKey}
                    onChange={onHandleSearchKey}
                    placeholder="Search coupon"
                  />
                </div>
                <div className="h-[48px] w-[30%]">
                  <Select
                    className="w-full h-full"
                    showSearch={false}
                    onChange={(val) => setStatus(val)}
                    placeholder="Status"
                    optionFilterProp="label"
                    options={[{ value: 'Active', label: 'Active' },
                      { value: 'Inactive', label: 'Inactive' },
                      { value: 'Expired', label: 'Expired' },
                      { value: 'Upcoming', label: 'Upcoming' }]}
                    value={status}
                  />
                </div>
              </div>

              <div className="flex gap-x-[15px]">
                <ConfigProvider
                  theme={{
                    token: {
                      colorPrimary: "#000000",
                    },
                  }}
                >
                  <Button className="text-white font-semibold h-[48px]" type="primary" onClick={handleCancelSearch}>
                    Clear
                  </Button>
                </ConfigProvider>
                <ConfigProvider
                  theme={{
                    token: {
                      colorPrimary: "#FCBD10",
                      colorPrimaryHover: "#FCBD10",
                      colorPrimaryActive: "#FCBD10",
                      colorTextLightSolid: "#000000",
                    },
                  }}
                >
                  <Button className="text-black font-semibold h-[48px]" type="primary" onClick={handleApplySearch}>
                    Apply
                  </Button>
                </ConfigProvider>
              </div>
            </div>
          </div>

          {/* Body Section */}
          <div className="p-5 min-h-[70vh]">
            <ConfigProvider
              theme={{
                components: {
                  Table: {
                    headerBg: "#EFEFEF",
                  },
                },
              }}
            >
              <Table
                dataSource={couponList}
                columns={columns}
                rowKey="id"
                pagination={false}
                loading={networkCall}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <span className="font-bold text-[16px] text-gray-800 bg-yellow-100 px-3 py-1">
                          No coupons found
                        </span>
                      }
                    />
                  )
                }}
              />
            </ConfigProvider>
          </div>

          <div className="mx-7 mt-[20px] mb-[50px] report-pagination relative custom-pagination-options">
            <Pagination
              showSizeChanger
              onChange={handlePageChange}
              onShowSizeChange={(current, size) => {
                setPerPage(size);
              }}
              current={page}
              pageSize={perPage}
              total={total}
              pageSizeOptions={PAGINATION_PAGE_OPTIONS}
              locale={{ items_per_page: "" }}
              showSearch={false}
              showQuickJumper={false}
            />

            <div
              className="absolute h-[48px] flex items-center space-x-[40px] 
          top-0 right-[122px] text-[18px] font-medium text-[#7D7D7D] mr-3"
            >
              <div>Total: {total}</div>
              <div>Row </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}